import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Package, CreditCard, User, MapPin, Phone, Mail, Crown, Award, ShoppingBag, Calendar, Truck, CheckCircle, Clock, XCircle, Lock, Eye, EyeOff, Camera, Gift, Copy, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = () => {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Referral state
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    fetchOrders();
    if (user?.profile_photo) {
      setProfilePhoto(user.profile_photo);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'referrals') fetchReferralStats();
  }, [activeTab]);

  const fetchReferralStats = async () => {
    setReferralLoading(true);
    try {
      const res = await axios.get(`${API_URL}/referral/my-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setReferralStats(res.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be less than 2MB');
      return;
    }
    
    setUploadingPhoto(true);
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.put(`${API_URL}/auth/profile-photo`, 
          { profile_photo: reader.result },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setProfilePhoto(reader.result);
        toast.success('Profile photo updated');
      } catch (error) {
        toast.error('Failed to update profile photo');
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'shipped': return <Truck className="w-4 h-4 text-purple-600" />;
      case 'processing': return <Clock className="w-4 h-4 text-blue-600" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'referrals', label: 'Referrals', icon: Gift },
    { id: 'security', label: 'Security', icon: Lock },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  const pendingOrders = orders.filter(o => o.payment_status === 'pending_payment').length;
  const completedOrders = orders.filter(o => o.order_status === 'delivered').length;
  const totalSpent = orders.filter(o => o.payment_status === 'paid').reduce((sum, o) => sum + o.total, 0);

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-[#0A2342] mb-2">
              Welcome back, {user?.first_name}!
            </h1>
            <p className="text-gray-600">Manage your account and track your orders</p>
          </div>
          {(user?.is_operator || user?.email?.endsWith('@justdive.co.za')) && (
            <Link
              to="/planner"
              className="inline-flex items-center gap-2 bg-[#0A2342] text-white hover:bg-[#0A2342]/90 px-4 py-2.5 rounded-lg shadow-sm transition w-full md:w-auto justify-center"
              data-testid="dashboard-planner-link"
            >
              <Calendar className="w-4 h-4" />
              <span className="font-medium">Open Dive Planner</span>
            </Link>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-gray-200 pb-4">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'bg-[#0A2342]' : ''}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Membership Card */}
            <div className={`relative overflow-hidden rounded-2xl p-8 ${
              user?.membership_tier === 'pro' 
                ? 'bg-gradient-to-br from-[#FF6B35] to-[#e85a2c]' 
                : user?.membership_tier === 'club'
                ? 'bg-gradient-to-br from-[#0A2342] to-[#1a3a5c]'
                : 'bg-gradient-to-br from-gray-600 to-gray-800'
            }`}>
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white transform translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white transform -translate-x-1/2 translate-y-1/2"></div>
              </div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {user?.membership_tier === 'pro' ? (
                        <Crown className="w-6 h-6 text-white" />
                      ) : user?.membership_tier === 'club' ? (
                        <Award className="w-6 h-6 text-white" />
                      ) : (
                        <User className="w-6 h-6 text-white" />
                      )}
                      <span className="text-white/80 text-sm uppercase tracking-wider">
                        {user?.membership_tier ? 'Member' : 'Guest'}
                      </span>
                    </div>
                    <h2 className="text-3xl font-heading font-bold text-white">
                      {user?.membership_tier === 'pro' 
                        ? 'Just Dive Pro' 
                        : user?.membership_tier === 'club'
                        ? 'Just Dive Club'
                        : 'No Membership'}
                    </h2>
                  </div>
                  <div className="text-right">
                    <img 
                      src="/logo-light.png" 
                      alt="Just Dive" 
                      className="h-10 opacity-90"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                </div>

                {user?.membership_tier ? (
                  <div className="space-y-4">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Membership Number</p>
                      <p className="text-white text-2xl font-mono-accent font-bold tracking-wider">
                        {user?.membership_number || 'Pending'}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Member Name</p>
                        <p className="text-white font-semibold">
                          {user?.first_name} {user?.last_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Discount</p>
                        <p className="text-white font-semibold text-xl">
                          {user?.membership_tier === 'pro' ? '25%' : '20%'} OFF
                        </p>
                      </div>
                    </div>

                    {user?.membership_expiry && (
                      <div className="pt-2 border-t border-white/20">
                        <p className="text-white/70 text-xs">
                          Valid until {new Date(user.membership_expiry).toLocaleDateString('en-ZA', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-white/80">
                      Join our membership program to enjoy exclusive discounts on all diving gear!
                    </p>
                    <Link to="/membership">
                      <Button className="bg-white text-[#0A2342] hover:bg-gray-100">
                        Become a Member
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#0A2342] rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Total Orders</span>
                  </div>
                  <p className="text-3xl font-mono-accent font-bold text-[#0A2342]">{orders.length}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Pending</span>
                  </div>
                  <p className="text-3xl font-mono-accent font-bold text-[#0A2342]">{pendingOrders}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Completed</span>
                  </div>
                  <p className="text-3xl font-mono-accent font-bold text-[#0A2342]">{completedOrders}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#FF6B35] rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Total Spent</span>
                  </div>
                  <p className="text-3xl font-mono-accent font-bold text-[#0A2342]">R{totalSpent.toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>

            {/* Account Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#0A2342]">
                  <User className="w-5 h-5" />
                  Account Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Profile Photo */}
                  <div className="md:col-span-2 flex justify-center mb-4">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                        {profilePhoto ? (
                          <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-10 h-10 text-gray-400" />
                        )}
                      </div>
                      <label className="absolute bottom-0 right-0 bg-[#FF6B35] text-white rounded-full p-2 cursor-pointer hover:bg-[#FF6B35]/90 transition-colors">
                        {uploadingPhoto ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Camera className="w-4 h-4" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploadingPhoto}
                          data-testid="profile-photo-input"
                        />
                      </label>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Full Name</p>
                        <p className="font-semibold text-[#0A2342]">{user?.first_name} {user?.last_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Email</p>
                        <p className="font-semibold text-[#0A2342]">{user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Member Since</p>
                        <p className="font-semibold text-[#0A2342]">{user?.date_joined || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Contact Number</p>
                        <p className="font-semibold text-[#0A2342]">{user?.contact_number || 'Not provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Address</p>
                        <p className="font-semibold text-[#0A2342]">{user?.physical_address || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Orders Preview */}
            {orders.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-[#0A2342]">
                    <Package className="w-5 h-5" />
                    Recent Orders
                  </CardTitle>
                  <Button variant="ghost" onClick={() => setActiveTab('orders')} className="text-[#FF6B35]">
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {orders.slice(0, 3).map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          {getStatusIcon(order.order_status)}
                          <div>
                            <p className="font-mono-accent text-sm font-semibold">
                              #{order.id.slice(0, 8).toUpperCase()}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono-accent font-bold text-[#0A2342]">R{order.total.toFixed(2)}</p>
                          <Badge variant="outline" className="text-xs">
                            {order.order_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0A2342]">
                <Package className="w-5 h-5" />
                Order History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-xl text-gray-500 mb-2">No orders yet</p>
                  <p className="text-gray-400 mb-6">Start shopping to see your orders here</p>
                  <Link to="/products">
                    <Button className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90">
                      Browse Products
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="border border-gray-200 rounded-lg p-6 hover:border-[#0A2342]/20 hover:shadow-md transition-all"
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-mono-accent text-lg font-bold text-[#0A2342] mb-2">
                            Order #{order.id.slice(0, 8).toUpperCase()}
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            <Badge
                              className={
                                order.payment_status === 'paid' 
                                  ? 'bg-green-100 text-green-800 border-green-200' 
                                  : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                              }
                            >
                              {order.payment_status === 'pending_payment' ? 'Awaiting Payment' : 
                               order.payment_status === 'paid' ? 'Paid' : order.payment_status}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={
                                order.order_status === 'delivered' ? 'border-green-600 text-green-600' :
                                order.order_status === 'shipped' ? 'border-purple-600 text-purple-600' :
                                order.order_status === 'processing' ? 'border-blue-600 text-blue-600' :
                                order.order_status === 'cancelled' ? 'border-red-600 text-red-600' :
                                'border-gray-400 text-gray-600'
                              }
                            >
                              {getStatusIcon(order.order_status)}
                              <span className="ml-1">
                                {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                              </span>
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-mono-accent font-bold text-[#0A2342]">
                            R{order.total.toFixed(2)}
                          </p>
                          {order.discount_amount > 0 && (
                            <p className="text-sm text-[#FF6B35] font-semibold">
                              Saved R{order.discount_amount.toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-xs text-gray-500 uppercase mb-2">Items</p>
                        <div className="space-y-2">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-700">
                                {item.name} 
                                {item.size && <span className="text-gray-500"> (Size: {item.size})</span>}
                                <span className="text-gray-500"> × {item.quantity}</span>
                              </span>
                              <span className="font-mono-accent font-semibold">R{item.total.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>Ordered on {new Date(order.created_at).toLocaleDateString('en-ZA', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                        {order.shipping_cost > 0 && (
                          <span>Shipping: R{order.shipping_cost.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            {referralLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0A2342]" /></div>
            ) : referralStats ? (
              <>
                {/* Referral Link Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#0A2342] flex items-center gap-2">
                      <Gift className="w-5 h-5 text-[#FF6B35]" />
                      Your Referral Link
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">Share this link with friends. When they sign up and book a Discover Scuba, you both earn rewards!</p>
                    <div className="flex gap-2">
                      <Input value={referralStats.referral_link} readOnly className="font-mono text-sm bg-gray-50" data-testid="referral-link-input" />
                      <Button variant="outline" onClick={() => {
                        navigator.clipboard.writeText(referralStats.referral_link);
                        setLinkCopied(true);
                        toast.success('Link copied!');
                        setTimeout(() => setLinkCopied(false), 3000);
                      }} data-testid="copy-referral-link">
                        {linkCopied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    {/* Share buttons */}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralStats.referral_link + '&utm_source=facebook')}`;
                        window.open(url, '_blank', 'width=600,height=400');
                      }} data-testid="share-facebook">
                        <Share2 className="w-4 h-4 mr-1" /> Facebook
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => {
                        navigator.clipboard.writeText(referralStats.referral_link + '&utm_source=instagram');
                        toast.success('Link copied for Instagram!');
                      }}>
                        <Copy className="w-4 h-4 mr-1" /> Copy for Instagram
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card><CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-[#0A2342]">{referralStats.stats.clicked}</div>
                    <div className="text-xs text-gray-500">Invited</div>
                  </CardContent></Card>
                  <Card><CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{referralStats.stats.signed_up}</div>
                    <div className="text-xs text-gray-500">Signed Up</div>
                  </CardContent></Card>
                  <Card><CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-yellow-600">{referralStats.stats.booked}</div>
                    <div className="text-xs text-gray-500">Booked</div>
                  </CardContent></Card>
                  <Card><CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{referralStats.stats.completed}</div>
                    <div className="text-xs text-gray-500">Completed</div>
                  </CardContent></Card>
                </div>

                {/* Progress to Next Reward */}
                {referralStats.next_tier && (
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-[#0A2342] mb-2">Next Reward</h3>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">{referralStats.next_tier.reward_value}</span>
                        <span className="text-sm font-medium">{referralStats.stats.completed} / {referralStats.next_tier.threshold}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="bg-[#FF6B35] h-3 rounded-full transition-all" style={{ width: `${Math.min(100, (referralStats.stats.completed / referralStats.next_tier.threshold) * 100)}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {referralStats.next_tier.remaining} more completed referral{referralStats.next_tier.remaining !== 1 ? 's' : ''} to unlock <strong>{referralStats.next_tier.reward_value}</strong>
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Active Rewards */}
                {(referralStats.rewards.length > 0 || referralStats.friend_rewards.length > 0) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#0A2342]">Your Rewards</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {[...referralStats.friend_rewards, ...referralStats.rewards].map((reward) => (
                        <div key={reward.id} className={`p-4 rounded-lg border ${reward.status === 'REDEEMED' ? 'bg-gray-50 opacity-60' : 'bg-[#FFF3ED] border-[#FF6B35]/20'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-[#0A2342]">{reward.reward_value}</div>
                              <div className="text-xs text-gray-500">
                                Code: <span className="font-mono font-bold text-[#FF6B35]">{reward.voucher_code}</span>
                              </div>
                            </div>
                            <Badge className={reward.status === 'REDEEMED' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}>
                              {reward.status === 'REDEEMED' ? 'Redeemed' : 'Active'}
                            </Badge>
                          </div>
                          {reward.expiry_date && reward.status !== 'REDEEMED' && (
                            <div className="text-xs text-gray-400 mt-1">
                              Expires: {new Date(reward.expiry_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* How it works */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-[#0A2342] mb-3">How It Works</h3>
                    <div className="space-y-3 text-sm text-gray-600">
                      <div className="flex gap-3"><span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs shrink-0">1</span>Share your referral link with friends</div>
                      <div className="flex gap-3"><span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs shrink-0">2</span>They sign up and get a FREE Discover Scuba voucher</div>
                      <div className="flex gap-3"><span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs shrink-0">3</span>When they book their Discover Scuba, you earn rewards!</div>
                    </div>
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                      Referrals only count once your friend signs up and books a Discover Scuba.
                    </div>
                    <div className="mt-3 space-y-1 text-xs text-gray-500">
                      <p><strong>3 completed referrals</strong> = Free Refresher Course for you</p>
                      <p><strong>5 completed referrals</strong> = R1,000 off your next purchase</p>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card><CardContent className="pt-6 text-center text-gray-500">Failed to load referral data. Please try again.</CardContent></Card>
            )}
          </div>
        )}

        {activeTab === 'security' && (
          <div className="max-w-xl">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#0A2342]">
                  <Lock className="w-5 h-5" />
                  Change Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div>
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative mt-2">
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        required
                        placeholder="Enter your current password"
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative mt-2">
                      <Input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        required
                        placeholder="Enter new password (min. 8 characters)"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <div className="relative mt-2">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        required
                        placeholder="Confirm your new password"
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <Button
                      type="submit"
                      disabled={passwordLoading}
                      className="w-full bg-[#0A2342] text-white hover:bg-[#0A2342]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                      data-testid="change-password-btn"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating Password...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    Make sure to use a strong password that you don't use on other sites.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
